// Upgrades plain <table> markup authored via the Rich Text Editor into an
// accessible table at render time (thead/th/scope, wrapped for horizontal
// scroll per WCAG 1.4.10's data-table exception), and removes any leftover
// CM-authoring "shadow" preview tables that were accidentally persisted
// into saved field HTML.

const PROCESSED_ATTR = 'data-rt-enhanced';
const COMPLEX_ATTR = 'data-complex-table';
const ORPHAN_SHADOW_SELECTOR = '.responsive-table.shadow, table[id^="shadow_rt_"]';

function isExperienceEditor() {
  return !!document.querySelector('.pageedit');
}

function removeOrphanShadowTables() {
  document.querySelectorAll(ORPHAN_SHADOW_SELECTOR).forEach(shadow => shadow.remove());
}

function isComplexTable(table) {
  return !!table.querySelector('[rowspan], [colspan]');
}

function wrapWithScrollContainer(table) {
  if (table.parentElement && table.parentElement.classList.contains('table-scroll')) return;

  const wrapper = document.createElement('div');
  wrapper.classList.add('table-scroll');
  table.parentNode.insertBefore(wrapper, table);
  wrapper.appendChild(table);
}

// Builds a real <thead><th scope="col"> header from the first row (or adds
// scope to an existing, manually-authored <thead>) and returns the header labels.
function buildHeader(table) {
  const existingHeaderRow = table.querySelector('thead tr');
  if (existingHeaderRow) {
    const headerCells = Array.from(existingHeaderRow.querySelectorAll('th'));
    headerCells.forEach(th => {
      if (!th.hasAttribute('scope')) th.setAttribute('scope', 'col');
    });
    return headerCells.map(th => th.innerHTML);
  }

  const firstRow = table.querySelector('tr');
  if (!firstRow) return null;

  const thead = document.createElement('thead');
  firstRow.remove();
  thead.appendChild(firstRow);
  table.prepend(thead);

  return Array.from(firstRow.querySelectorAll('td, th')).map(cell => {
    const th = document.createElement('th');
    th.innerHTML = cell.innerHTML;
    th.setAttribute('scope', 'col');
    cell.parentNode.replaceChild(th, cell);
    return th.innerHTML;
  });
}

function wrapBodyRows(table) {
  if (table.querySelector('tbody')) return;

  const tbody = document.createElement('tbody');
  Array.from(table.children)
    .filter(child => child.tagName === 'TR')
    .forEach(row => tbody.appendChild(row));
  table.appendChild(tbody);
}

function enhanceTable(table) {
  if (table.hasAttribute(PROCESSED_ATTR)) return;
  table.setAttribute(PROCESSED_ATTR, 'true');

  wrapWithScrollContainer(table);

  if (isComplexTable(table)) {
    // Rowspan/colspan requires explicit id/headers associations that can't
    // be safely inferred - leave the table untouched rather than guess wrong.
    table.setAttribute(COMPLEX_ATTR, 'unhandled');
    console.warn('[enhanceRichTextTables] Skipped table with rowspan/colspan - needs manual id/headers associations.', table);
    return;
  }

  const headerLabels = buildHeader(table);
  if (!headerLabels || !headerLabels.length) return;

  table.classList.add('responsive-table');
  wrapBodyRows(table);
}

export function enhanceRichTextTables() {
  if (isExperienceEditor()) return;

  removeOrphanShadowTables();

  // Isolated per table: one malformed/unexpected table throwing must not stop
  // every other table on the page from being enhanced, since forEach has no
  // built-in error isolation between iterations.
  document.querySelectorAll('table').forEach(table => {
    try {
      enhanceTable(table);
    } catch (err) {
      console.error('[enhanceRichTextTables] Failed to enhance table', table, err);
    }
  });
}
